import React from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Previous/Next pagination controls for navigating between pages.
 * Optionally includes a page size selector.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.hasPrevious - Whether there is a previous page
 * @param {boolean} props.hasNext - Whether there is a next page
 * @param {() => void} props.onPrevious - Callback to go to previous page
 * @param {() => void} props.onNext - Callback to go to next page
 * @param {boolean} [props.isLoading=false] - Whether pagination is loading
 * @param {number} [props.currentPage] - Current page number (1-indexed, for display)
 * @param {number} [props.totalPages] - Total number of pages (for display)
 * @param {number} [props.pageSize] - Current page size (enables page size selector when provided)
 * @param {(size: number) => void} [props.onPageSizeChange] - Callback when page size changes
 * @param {number[]} [props.pageSizeOptions=[10,25,50,100]] - Available page size options
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {JSX.Element} Pagination controls
 *
 * @example
 * <PaginationControls
 *   hasPrevious={pageIndex > 0}
 *   hasNext={hasNextPage}
 *   onPrevious={() => setPageIndex(p => p - 1)}
 *   onNext={() => setPageIndex(p => p + 1)}
 *   currentPage={pageIndex + 1}
 *   totalPages={Math.ceil(totalCount / pageSize)}
 *   pageSize={50}
 *   onPageSizeChange={(size) => setPageSize(size)}
 * />
 */
export function PaginationControls({
    className = '',
    currentPage,
    hasNext,
    hasPrevious,
    isLoading = false,
    onNext,
    onPageSizeChange,
    onPrevious,
    pageSize,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    totalPages,
}) {
    const showPageInfo = currentPage !== undefined && totalPages !== undefined;
    const showPageSizeSelector = pageSize !== undefined && onPageSizeChange !== undefined;

    return (
        <div className={`flex items-center justify-center gap-4 py-4 ${className}`}>
            {/* Page size selector */}
            {showPageSizeSelector && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows:</span>
                    <Select
                        value={String(pageSize)}
                        onValueChange={(value) => onPageSizeChange(Number(value))}
                        disabled={isLoading}
                    >
                        <SelectTrigger className="h-8 w-[70px]" aria-label="Select page size">
                            <SelectValue placeholder={pageSize} />
                        </SelectTrigger>
                        <SelectContent>
                            {pageSizeOptions.map((size) => (
                                <SelectItem key={size} value={String(size)}>
                                    {size}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <Button
                variant="outline"
                size="sm"
                onClick={onPrevious}
                disabled={!hasPrevious || isLoading}
                aria-label="Go to previous page"
            >
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
                Previous
            </Button>

            {showPageInfo && (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                        <>
                            Page {currentPage} of {totalPages}
                        </>
                    )}
                </span>
            )}

            {isLoading && !showPageInfo && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
            )}

            <Button
                variant="outline"
                size="sm"
                onClick={onNext}
                disabled={!hasNext || isLoading}
                aria-label="Go to next page"
            >
                Next
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
}

export default PaginationControls;
